import { useEffect, useState } from "react";
import { api } from "@/contexts/AuthContext";
import { useAuth } from "@/contexts/AuthContext";
import { Navigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Mail, RotateCcw, Save, Copy } from "lucide-react";

const PLACEHOLDER_HINTS = {
  visitor_name: "Visitor's full name",
  arrival_date: "Arrival date",
  departure_date: "Departure date",
  specialty: "Medical specialty",
  department: "Assigned department",
  party_size: "Number of visitors",
  housing: "Assigned housing (approval email only)",
  reason: "Reason / notes (rejection email only)",
};

function TemplateEditor({ type, template, placeholders, onSave, onReset, saving }) {
  const [subject, setSubject] = useState(template.subject);
  const [body, setBody] = useState(template.body);

  useEffect(() => {
    setSubject(template.subject);
    setBody(template.body);
  }, [template]);

  const insertPlaceholder = (ph) => {
    const token = `{${ph}}`;
    setBody((prev) => prev + token);
    navigator.clipboard?.writeText(token).catch(() => {});
    toast.success(`Inserted ${token}`);
  };

  const preview = body
    .replace(/\{visitor_name\}/g, "Dr. Jane Doe")
    .replace(/\{arrival_date\}/g, "2026-05-10")
    .replace(/\{departure_date\}/g, "2026-05-17")
    .replace(/\{specialty\}/g, "Cardiology")
    .replace(/\{department\}/g, "Medicine")
    .replace(/\{party_size\}/g, "2")
    .replace(/\{housing\}/g, "Guest House A — Room 3")
    .replace(/\{reason\}/g, "Overlap with existing visitor schedule");

  return (
    <div className="space-y-6" data-testid={`template-editor-${type}`}>
      <div className="space-y-2">
        <label className="text-sm font-medium text-[#1C1F1E]">Subject</label>
        <Input
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          data-testid={`${type}-subject-input`}
          className="font-mono text-sm"
        />
      </div>

      <div className="space-y-2">
        <label className="text-sm font-medium text-[#1C1F1E]">Body</label>
        <Textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={14}
          data-testid={`${type}-body-input`}
          className="font-mono text-sm leading-relaxed"
        />
      </div>

      <div className="space-y-2">
        <p className="text-xs uppercase tracking-wide text-[#7A8A84] font-medium">
          Insert placeholder
        </p>
        <div className="flex flex-wrap gap-2">
          {placeholders.map((ph) => (
            <button
              key={ph}
              type="button"
              onClick={() => insertPlaceholder(ph)}
              data-testid={`placeholder-${ph}`}
              className="group inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[#F4F4F1] hover:bg-[#1B4332] hover:text-white text-xs font-mono text-[#1C1F1E] transition-colors border border-[#E8E8E6]"
              title={PLACEHOLDER_HINTS[ph] || ph}
            >
              <Copy className="h-3 w-3" />
              {`{${ph}}`}
            </button>
          ))}
        </div>
        <p className="text-xs text-[#7A8A84]">
          Click a chip to append it to the body (it's also copied to your clipboard).
        </p>
      </div>

      <div className="border border-[#E8E8E6] rounded-lg overflow-hidden">
        <div className="bg-[#F4F4F1] px-4 py-2 text-xs font-medium text-[#7A8A84] border-b border-[#E8E8E6]">
          Preview with sample data
        </div>
        <div className="bg-white p-4">
          <div className="text-sm font-semibold text-[#1C1F1E] mb-2" data-testid={`${type}-preview-subject`}>
            {subject
              .replace(/\{visitor_name\}/g, "Dr. Jane Doe")
              .replace(/\{arrival_date\}/g, "2026-05-10")
              .replace(/\{departure_date\}/g, "2026-05-17")}
          </div>
          <pre className="text-sm text-[#1C1F1E] whitespace-pre-wrap font-sans leading-relaxed" data-testid={`${type}-preview-body`}>
            {preview}
          </pre>
        </div>
      </div>

      <div className="flex items-center justify-between pt-2">
        <Button
          variant="ghost"
          onClick={() => onReset(type)}
          disabled={saving}
          data-testid={`${type}-reset-btn`}
          className="text-[#7A8A84] hover:text-[#1C1F1E]"
        >
          <RotateCcw className="h-4 w-4 mr-2" />
          Reset to default
        </Button>
        <Button
          onClick={() => onSave(type, { subject, body })}
          disabled={saving}
          data-testid={`${type}-save-btn`}
          className="bg-[#1B4332] hover:bg-[#143126]"
        >
          <Save className="h-4 w-4 mr-2" />
          {saving ? "Saving..." : "Save template"}
        </Button>
      </div>

      {template.updated_by_name && (
        <p className="text-xs text-[#7A8A84]">
          Last updated by <strong>{template.updated_by_name}</strong>
          {template.updated_at && (
            <> on {new Date(template.updated_at).toLocaleString()}</>
          )}
        </p>
      )}
    </div>
  );
}

export default function EmailTemplates() {
  const { user } = useAuth();
  const [templates, setTemplates] = useState(null);
  const [placeholders, setPlaceholders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/email-templates");
      setTemplates(data.templates);
      setPlaceholders(data.placeholders);
    } catch (e) {
      toast.error("Failed to load templates");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  if (user?.role !== "admin") {
    return <Navigate to="/dashboard" replace />;
  }

  const handleSave = async (type, payload) => {
    setSaving(true);
    try {
      const { data } = await api.put(`/email-templates/${type}`, payload);
      setTemplates((prev) => ({ ...prev, [type]: data }));
      toast.success(`${type === "approval" ? "Approval" : "Rejection"} template saved`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async (type) => {
    if (!window.confirm(`Reset the ${type} template to its default?`)) return;
    setSaving(true);
    try {
      const { data } = await api.post(`/email-templates/${type}/reset`);
      setTemplates((prev) => ({ ...prev, [type]: data }));
      toast.success("Reset to default");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reset failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading || !templates) {
    return <div className="text-[#7A8A84]" data-testid="templates-loading">Loading templates…</div>;
  }

  return (
    <div className="max-w-4xl" data-testid="email-templates-page">
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-lg bg-[#1B4332]/10 flex items-center justify-center">
            <Mail className="h-5 w-5 text-[#1B4332]" />
          </div>
          <h1 className="text-2xl font-semibold text-[#1C1F1E]" style={{ fontFamily: "Manrope, sans-serif" }}>
            Email Templates
          </h1>
        </div>
        <p className="text-sm text-[#7A8A84] max-w-2xl">
          Customize the approval and rejection emails sent to stakeholders automatically
          when a visitor request is finalized. Use the chips below to insert placeholders
          that will be replaced with real data when the email is sent.
        </p>
      </div>

      <Card className="border-[#E8E8E6]">
        <CardHeader className="pb-0">
          <CardTitle className="text-sm text-[#7A8A84] font-medium">
            Choose a template to edit
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-4">
          <Tabs defaultValue="approval" className="w-full">
            <TabsList className="grid grid-cols-2 max-w-md mb-6" data-testid="template-tabs">
              <TabsTrigger value="approval" data-testid="tab-approval">Approval</TabsTrigger>
              <TabsTrigger value="rejection" data-testid="tab-rejection">Rejection</TabsTrigger>
            </TabsList>
            <TabsContent value="approval">
              <TemplateEditor
                type="approval"
                template={templates.approval}
                placeholders={placeholders}
                onSave={handleSave}
                onReset={handleReset}
                saving={saving}
              />
            </TabsContent>
            <TabsContent value="rejection">
              <TemplateEditor
                type="rejection"
                template={templates.rejection}
                placeholders={placeholders}
                onSave={handleSave}
                onReset={handleReset}
                saving={saving}
              />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}
